import { useEffect, useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";
import { useWindowSize } from "usehooks-ts";

import { AiLogo } from "@/components/ai-logo/AiLogo";
import ChatInputBar from "@/components/chat/chat-input/ChatInputBar";
import { useBrowserKeyboardInset } from "@/components/chat/chatKeyboardAvoidance";
import { AddBlockButton } from "@/components/doc/AddBlockButton";
import { SuggestionsProvider } from "@/components/suggestions/SuggestionsProvider";
import { SHOW_CHAT_INPUT_MOBILE_ID } from "@/components/VisibilityToggle";
import { useFloatingChatInputWidth } from "@/models/chat/chatHelpers";
import { useStore } from "@/store";
import { getLatestChat } from "@/store/ChatsStore";
import { useChats } from "@/store/useChats";
import { SCREEN_SM } from "@/style/breakpoints";
import { twMerge } from "@/utils/twMerge";

const FloatingChatInput = observer(() => {
  const t = useTranslations();
  const chats = useChats();
  const { view } = useStore();
  const floatingInputWidth = useFloatingChatInputWidth();
  const chat = getLatestChat(chats.chatsWithBot);
  const { doc } = chats;
  const docId = doc?.id;
  const canComment = doc?.permissions.comment ?? false;
  const { width: windowWidth } = useWindowSize();
  const isMobile = windowWidth < SCREEN_SM;
  const [isInputFocused, setIsInputFocused] = useState(false);
  const keyboardInset = useBrowserKeyboardInset(isMobile && isInputFocused);

  useEffect(() => {
    if (!canComment) return;
    chats.ensureBotChatForCurrentDoc();
  }, [chats, docId, canComment]);

  if (chats.isOpenChat || view.areSidebarsHidden) {
    return null;
  }

  // The "Add block" menu sits left of the chat input on mobile, so blocks can be
  // added without opening the keyboard. It makes room for the input once
  // that's focused.
  const addBlockButton = isMobile && doc ? <AddBlockButton doc={doc} /> : null;
  const isChatHidden = chats.botChatDisabled || !chat || view.isFloatingChatInputHidden;

  if (isChatHidden) {
    if (!isMobile) return null;
    return (
      <div className="fixed right-5 bottom-5 z-20 flex items-center gap-2">
        {addBlockButton}
        {view.isFloatingChatInputHidden && !chats.botChatDisabled && (
          <button
            id={SHOW_CHAT_INPUT_MOBILE_ID}
            className={twMerge(
              "w-14.5 h-13.5 rounded-[20px] flex items-center justify-center",
              "bg-document-bg chat-input-surface",
            )}
            type="button"
            onClick={() => view.toggleFloatingChatInput(false)}
            aria-label={t("chat.showChatInput")}
          >
            <AiLogo state="idle" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={twMerge("sticky bottom-0 h-0 mt-auto z-20", doc?.isFocused && "sm:hidden")}>
      <div
        className="absolute bottom-5 left-1/2 -translate-x-1/2 max-w-[calc(100%-20px)] sm:fixed flex items-end gap-2"
        style={{
          width: floatingInputWidth,
          // Keep the focused input above the on-screen keyboard (iOS Safari
          // doesn't resize the layout viewport for it).
          ...(keyboardInset > 0 && { position: "fixed", bottom: keyboardInset + 20 }),
        }}
      >
        {/* Hidden rather than unmounted while typing, so a file picker opened from
            its menu can still report back. */}
        {addBlockButton && (
          <div hidden={isInputFocused} className="shrink-0">
            {addBlockButton}
          </div>
        )}
        <div className="animate-floating-chat-input flex-1 min-w-0">
          <SuggestionsProvider suggestionsAnchorId="chat-tiptap" suggestionsRenderPlace="chat">
            <ChatInputBar chat={chat} isFloatingInput onFocusChange={setIsInputFocused} />
          </SuggestionsProvider>
        </div>
      </div>
    </div>
  );
});

export default FloatingChatInput;
